import React, { useRef, useMemo, useState, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionValueEvent } from "framer-motion";
import styles from "./HomeImageHero.module.css";
import hero_image from "../../assets/images/Home/ifillip_school_students.png"

/* ------------------------------------------------------------------
   Default slide content   replace with CMS / Word-doc content later.
------------------------------------------------------------------ */
const DEFAULT_SLIDES = [
  {
    title: "AI-Powered Education & School Management",
    subtitle: "Transforming education with intelligent automation",
  },
  {
    title: "Connected Learning Ecosystem",
    subtitle: "Students, teachers, and parents in single platform",
  },
  {
    title: "Future-Ready Education",
    subtitle: "Smart insights, deeper student engagements, better outcomes, stronger institutions",
  },
];


/* ------------------------------------------------------------------
   Slide   handles its own fade/slide based on shared scroll progress.
------------------------------------------------------------------ */
const Slide = ({ slide, index, total, progress }) => {
  // Each slide owns a window of the total scroll.
  // Window size is 1/total, centered around its index.
  const start = index / total;
  const end = (index + 1) / total;

  // Fade + y transform curve:
  //   before its window  → opacity 0, y = 40  (waiting below)
  //   entering           → opacity 0→1, y = 40→0
  //   mid of window      → opacity 1, y = 0
  //   leaving            → opacity 1→0, y = 0→-30
  //   after              → opacity 0, y = -30
  const fadeIn = 0.08;   // portion of window used for entering
  const fadeOut = 0.08;  // portion of window used for exiting

  // Special-case the first slide so it's fully visible at progress = 0
  // (i.e. on initial mount, before the user scrolls). Without this, the
  // opacity curve interpolates 0 → 1 across [0, fadeIn], so at progress = 0
  // the first slide sits at opacity 0 until the user nudges the page.
  const isFirst = index === 0;

  const opacity = useTransform(
    progress,
    isFirst
      ? [0, end - fadeOut, Math.min(1, end + 0.01)]
      : [
          Math.max(0, start - 0.01),
          start + fadeIn,
          end - fadeOut,
          Math.min(1, end + 0.01),
        ],
    isFirst ? [1, 1, 0] : [0, 1, 1, 0]
  );

  const y = useTransform(
    progress,
    isFirst
      ? [0, end - fadeOut, Math.min(1, end + 0.01)]
      : [
          Math.max(0, start - 0.01),
          start + fadeIn,
          end - fadeOut,
          Math.min(1, end + 0.01),
        ],
    isFirst ? [0, 0, -30] : [40, 0, 0, -30]
  );

  return (
    <motion.div
      className={styles.slide}
      style={{ opacity, y }}
      aria-hidden={false}
    >
      <motion.h1 className={styles.title}>{slide.title}</motion.h1>
      <motion.p className={styles.subtitle}>{slide.subtitle}</motion.p>
    </motion.div>
  );
};

/* ------------------------------------------------------------------
   HomeImageHero   scroll-driven storytelling hero section.
------------------------------------------------------------------ */
const HomeImageHero = ({
  slides = DEFAULT_SLIDES,
  backgroundImage = hero_image,
  /* Multiplier of 100vh per slide worth of scroll   controls pacing.
     Higher = slower / more deliberate. */
  scrollPerSlide = 1,
}) => {
  const containerRef = useRef(null);

  // Memoise slides array so child hooks don't re-subscribe unnecessarily.
  const slidesMemo = useMemo(() => slides, [slides]);

  // Track scroll of the tall outer container relative to viewport.
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // Smooth the raw progress so transitions feel premium, not jittery.
  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 28,
    mass: 0.4,
    restDelta: 0.001,
  });

  // Track which slide is currently active (for orange dot highlighting).
  const [activeIndex, setActiveIndex] = useState(0);

  useMotionValueEvent(smoothProgress, "change", (latest) => {
    // Map progress [0, 1] → slide index [0, total - 1].
    // Clamp to valid range so the last slide stays active at the very end.
    const idx = Math.min(
      slidesMemo.length - 1,
      Math.floor(latest * slidesMemo.length)
    );
    setActiveIndex(idx);
  });

  // Click-to-scroll handler: scrolls the page so the targeted slide's
  // window sits in the middle of its scroll range (i.e. fully visible,
  // mid-way through its fade-in/fade-out window).
  const scrollToSlide = (index) => {
    const node = containerRef.current;
    if (!node) return;

    const rect = node.getBoundingClientRect();
    const containerTop = window.scrollY + rect.top;
    const scrollableHeight = node.offsetHeight - window.innerHeight;

    // Target the *middle* of slide i's scroll window for a fully-settled view.
    // For slide i (0-indexed) of N slides, mid-progress = (i + 0.5) / N.
    // Special-case slide 0 → progress 0 so we land at the very top.
    const targetProgress =
      index === 0 ? 0 : (index + 0.5) / slidesMemo.length;

    const targetY = containerTop + scrollableHeight * targetProgress;

    window.scrollTo({
      top: targetY,
      behavior: "smooth",
    });
  };

  // Parallax background   subtle scale + slow vertical drift.
  const bgScale = useTransform(smoothProgress, [0, 1], [1, 1.1]);
  const bgY = useTransform(smoothProgress, [0, 1], ["0%", "-4%"]);

  // Total outer scroll height: one viewport per slide (× multiplier).
  const outerHeight = `${slidesMemo.length * 100 * scrollPerSlide}vh`;

  return (
    <section
      ref={containerRef}
      className={styles.outer}
      style={{ height: outerHeight }}
      aria-label="Hero storytelling"
    >
      {/* Sticky viewport   the visible 100vh window that pins while outer scrolls */}
      <div className={styles.sticky}>
        {/* Parallax background */}
        <motion.div
          className={styles.background}
          style={{
            backgroundImage: `url(${backgroundImage})`,
            scale: bgScale,
            y: bgY,
          }}
        />

        {/* Dark gradient overlay for readability */}
        <div className={styles.overlay} />

        {/* Subtle radial vignette for depth */}
        <div className={styles.vignette} />

        {/* Slide stack */}
        <div className={styles.content}>
          {slidesMemo.map((slide, i) => (
            <Slide
              key={i}
              slide={slide}
              index={i}
              total={slidesMemo.length}
              progress={smoothProgress}
            />
          ))}
        </div>

        {/* Vertical progress dots */}
        <ProgressDots
          total={slidesMemo.length}
          progress={smoothProgress}
          activeIndex={activeIndex}
          onDotClick={scrollToSlide}
          slides={slidesMemo}
        />
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------
   ProgressDots   vertical indicator on the right side.
------------------------------------------------------------------ */
const ProgressDots = ({ total, progress, activeIndex, onDotClick, slides }) => {
  return (
    <div className={styles.dots} role="tablist" aria-label="Hero slide navigation">
      {Array.from({ length: total }).map((_, i) => (
        <Dot
          key={i}
          index={i}
          total={total}
          progress={progress}
          isActive={i === activeIndex}
          onClick={() => onDotClick(i)}
          label={slides[i]?.title}
        />
      ))}
    </div>
  );
};

const Dot = ({ index, total, progress, isActive, onClick, label }) => {
  const start = index / total;
  const end = (index + 1) / total;
  const pad = 0.05;

  const scale = useTransform(
    progress,
    [
      Math.max(0, start - pad),
      start + 0.02,
      end - 0.02,
      Math.min(1, end + pad),
    ],
    [1, 1.6, 1.6, 1]
  );

  const opacity = useTransform(
    progress,
    [
      Math.max(0, start - pad),
      start + 0.02,
      end - 0.02,
      Math.min(1, end + pad),
    ],
    [0.35, 1, 1, 0.35]
  );

  return (
    <motion.button
      type="button"
      onClick={onClick}
      className={`${styles.dot} ${isActive ? styles.dotActive : ""}`}
      style={{ scale, opacity }}
      role="tab"
      aria-selected={isActive}
      aria-label={label ? `Go to slide: ${label}` : `Go to slide ${index + 1}`}
    />
  );
};

export default HomeImageHero;